import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, useAuth } from '../App';
import SEO from '../components/SEO';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2, Camera, X, ArrowRight, ArrowLeft,
  Globe, LinkIcon, CheckCircle, ChevronRight, Eye, EyeOff, Gift, Phone
} from 'lucide-react';

const LOGO_LOCKUP = "/logo-lockup.png";
const LOGO_LIGHT = "/logo-light.png";
const LOGO_ICON = "/logo-icon.png";

const C = {
  bg: 'var(--pd-bg)', purple: 'var(--pd-purple)', cyan: 'var(--pd-cyan)', gold: 'var(--pd-gold)',
  green: 'var(--pd-green)', text: 'var(--pd-text)', text2: 'var(--pd-text2)', muted: 'var(--pd-muted)',
  border: 'var(--pd-border)', glass: 'var(--pd-glass)',
};

const TOPIC_OPTIONS = [
  'Technology', 'Business', 'Marketing', 'Health & Wellness', 'True Crime',
  'Comedy', 'Education', 'Science', 'Finance', 'Self-Improvement',
  'Sports', 'Politics', 'Entertainment', 'Startups', 'AI & Machine Learning',
  'Leadership', 'Parenting', 'Travel', 'Food', 'Music'
];

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, updateUser } = useAuth();
  const { t } = useTranslation();

  const [isRegister, setIsRegister] = useState(searchParams.get('mode') === 'register');
  const initialRole = searchParams.get('role');
  const validRole = initialRole && ['host', 'guest', 'both'].includes(initialRole) ? initialRole : '';
  const [step, setStep] = useState(validRole && searchParams.get('mode') === 'register' ? 2 : 1); // 1=role, 2=basics, 3=role-fields
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [referralInfo, setReferralInfo] = useState(null); // { code, referrerName }
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '', role: validRole, phone: '',
    // Host
    podcast_name: '', show_description: '', topics_covered: [],
    guest_types_wanted: '', platform_links: {},
    // Guest
    headline: '', bio: '', expertise_topics: [],
    previous_appearances: '', social_links: {},
    // Temp for link inputs
    _platformUrl: '', _socialUrl: '',
  });

  const [sidebarStats, setSidebarStats] = useState({ podcasters: 0, swaps: 0, subscribers: 0 });

  useEffect(() => {
    axios.get(`${API}/stats/public`).then(res => {
      const t = res.data?.totals || {};
      const tiers = res.data?.tiers || {};
      const paidSubs = (tiers.licensed || 0) + (tiers.featured || 0) + (tiers.powerhouse || 0);
      setSidebarStats({ podcasters: t.podcasters || 0, swaps: t.matches_accepted || 0, subscribers: paidSubs });
    }).catch(() => {});
  }, []);

  // Validate referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setIsRegister(true);
      axios.post(`${API}/referral/validate`, { code: refCode })
        .then(res => {
          if (res.data?.valid) {
            setReferralInfo({ code: refCode, referrerName: res.data.referrer_name });
          }
        }).catch(() => {});
    }
  }, []);

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const handleChange = (e) => set(e.target.name, e.target.value);

  const toggleTopic = (topic, field) => {
    const arr = formData[field];
    set(field, arr.includes(topic) ? arr.filter(t => t !== topic) : [...arr, topic]);
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Please select a JPEG, PNG, WebP, or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const removeAvatar = () => {
    setAvatarFile(null); setAvatarPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRegister && step < 3) { setStep(step + 1); return; }

    setLoading(true);
    try {
      if (isRegister) {
        if (!formData.name || !formData.email || !formData.password || !formData.role) {
          return toast.error('Please fill in all required fields');
        }
        if (formData.password !== formData.confirmPassword) {
          return toast.error('Passwords do not match');
        }
        if (formData.password.length < 6) {
          return toast.error('Password must be at least 6 characters');
        }
        if (!agreedToPrivacy) {
          toast.error('Please agree to the Privacy Policy to continue.');
          setLoading(false);
          return;
        }
        const payload = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          phone: formData.phone || null,
          podcast_name: formData.podcast_name || null,
          show_description: formData.show_description || null,
          topics_covered: formData.topics_covered,
          guest_types_wanted: formData.guest_types_wanted || null,
          platform_links: formData.platform_links,
          headline: formData.headline || null,
          bio: formData.bio || null,
          expertise_topics: formData.expertise_topics,
          previous_appearances: formData.previous_appearances || null,
          social_links: formData.social_links,
          referral_code: referralInfo?.code || null,
        };
        const regResult = await register(payload);

        if (avatarFile && regResult?.token) {
          try {
            const fd = new FormData();
            fd.append('file', avatarFile);
            await axios.post(`${API}/avatar/upload`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch {}
        }

        // Clear temporary auth header - user must verify email before getting a real session
        delete axios.defaults.headers.common['Authorization'];

        // Backend already sent OTP during registration, no need to resend
        toast.success('Account created! Please verify your email.');
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
        return;
      } else {
        if (!formData.email || !formData.password) {
          toast.error('Please enter your email and password');
          setLoading(false); return;
        }
        const result = await login(formData.email, formData.password);

        // Check if email not verified
        if (result?.email_not_verified) {
          try {
            await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
          } catch {}
          toast.info('Please verify your email to continue.');
          navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
          return;
        }

        toast.success('Welcome back!');
      }
      if (formData.email === 'admin@podcasters.directory') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const data = error.response?.data;
      if (data?.email_not_verified) {
        try {
          await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
        } catch {}
        toast.info('Please verify your email first.');
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
        return;
      }
      toast.error(data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep2 = formData.name && formData.email && formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const isHost = formData.role === 'host' || formData.role === 'both';
  const isGuest = formData.role === 'guest' || formData.role === 'both';

  /* ─── Role Selection Step ─── */
  const RoleStep = () => (
    <div className="space-y-5" data-testid="role-step">
      <div className="mb-2">
        <h1 className="text-2xl font-bold mb-2" style={{ color: C.text }} data-testid="auth-heading">{t('auth.howUse')}</h1>
        <p className="text-sm" style={{ color: C.text2 }}>{t('auth.chooseRole')}</p>
      </div>
      {[
        { key: 'host', label: t('auth.hostLabel'), desc: t('auth.hostDesc'), color: C.purple },
        { key: 'guest', label: t('auth.guestLabel'), desc: t('auth.guestDesc'), color: C.cyan },
        { key: 'both', label: t('auth.bothLabel'), desc: t('auth.bothDesc'), color: C.gold },
      ].map(r => (
        <button key={r.key} type="button" onClick={() => { set('role', r.key); setStep(2); }}
          className={`w-full text-left p-5 rounded-2xl border transition-all duration-200 group hover:scale-[1.02] ${formData.role === r.key ? 'ring-2' : ''}`}
          style={{
            background: formData.role === r.key ? `${r.color}12` : C.glass,
            borderColor: formData.role === r.key ? `${r.color}40` : C.border,
            ringColor: r.color,
          }}
          data-testid={`role-${r.key}-btn`}
        >
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full shrink-0" style={{ background: r.color }} />
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold flex items-center gap-2" style={{ color: C.text }}>
                {r.label}
                <ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-100 group- transition-all" style={{ color: r.color }} />
              </div>
              <p className="text-xs mt-0.5" style={{ color: C.text2 }}>{r.desc}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  /* ─── Basics Step (Name/Email/Pass/Avatar) ─── */
  const BasicsStep = () => (
    <div className="space-y-5" data-testid="basics-step">
      <div className="mb-2">
        <h1 className="text-2xl font-bold mb-2" style={{ color: C.text }}>{t('auth.createAccount')}</h1>
        <p className="text-sm" style={{ color: C.text2 }}>
          {t('auth.signingUpAs')} <span className="font-medium" style={{ color: formData.role === 'host' ? C.purple : formData.role === 'guest' ? C.cyan : C.gold }}>
            {formData.role === 'host' ? t('auth.hostLabel') : formData.role === 'guest' ? t('auth.guestLabel') : t('auth.bothLabel')}
          </span>
        </p>
      </div>

      {/* Avatar */}
      <div className="flex justify-center mb-1">
        <div className="relative group">
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleAvatarSelect} className="hidden" data-testid="avatar-file-input" />
          <button type="button" onClick={() => fileInputRef.current?.click()}
            className="w-20 h-20 rounded-2xl bg-white/[0.04] border-2 border-dashed border-white/[0.12] hover:border-[#22D3EE]/40 flex items-center justify-center overflow-hidden transition-all group-hover:bg-white/[0.06]"
            data-testid="avatar-upload-btn">
            {avatarPreview ? <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
              : <div className="text-center"><Camera className="w-5 h-5 text-white/25 mx-auto mb-0.5 group-hover:text-[#22D3EE]/60 transition-colors" /><span className="text-[9px] text-white/25">Photo</span></div>}
          </button>
          {avatarPreview && (
            <button type="button" onClick={removeAvatar}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500/90 flex items-center justify-center text-white hover:bg-red-500 transition-colors"
              data-testid="avatar-remove-btn"><X className="w-3 h-3" /></button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name" style={{ color: C.text2 }} className="text-sm">{t('auth.fullName')} *</Label>
        <Input id="name" name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange}
          className="h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl focus:border-[#22D3EE]/40 focus:ring-[#22D3EE]/15"
          data-testid="name-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email" style={{ color: C.text2 }} className="text-sm">{t('auth.email')} *</Label>
        <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange}
          className="h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl focus:border-[#22D3EE]/40 focus:ring-[#22D3EE]/15"
          data-testid="email-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" style={{ color: C.text2 }} className="text-sm flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5" /> Phone <span className="text-[10px] font-normal" style={{ color: C.muted }}>(optional)</span>
        </Label>
        <Input id="phone" name="phone" type="tel" placeholder="+1 (555) 123-4567" value={formData.phone} onChange={handleChange}
          className="h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl focus:border-[#22D3EE]/40 focus:ring-[#22D3EE]/15"
          data-testid="phone-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" style={{ color: C.text2 }} className="text-sm">{t('auth.password')} *</Label>
        <div className="relative">
          <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" value={formData.password} onChange={handleChange}
            className="h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl focus:border-[#22D3EE]/40 focus:ring-[#22D3EE]/15 pr-12"
            data-testid="password-input" />
          <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors" data-testid="toggle-password-register">
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword" style={{ color: C.text2 }} className="text-sm">Confirm Password *</Label>
        <div className="relative">
          <Input id="confirmPassword" name="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Re-enter your password" value={formData.confirmPassword} onChange={handleChange}
            className={`h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl focus:border-[#22D3EE]/40 focus:ring-[#22D3EE]/15 pr-12 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-500/50' : ''}`}
            data-testid="confirm-password-input" />
          <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors" data-testid="toggle-confirm-password-register">
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {formData.confirmPassword && formData.password !== formData.confirmPassword && (
          <p className="text-xs text-red-400">Passwords do not match</p>
        )}
      </div>

      <Button type="submit" className="w-full h-12 text-base rounded-xl mt-2"
        style={{ background: C.purple, color: '#fff' }}
        disabled={!canProceedStep2} data-testid="next-step-btn">
        {t('auth.continue')} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  /* ─── Role-Specific Fields Step ─── */
  const RoleFieldsStep = () => (
    <div className="space-y-5" data-testid="role-fields-step">
      <div className="mb-2">
        <h1 className="text-xl font-bold mb-1" style={{ color: C.text }}>{t('auth.completeProfile')}</h1>
        <p className="text-xs" style={{ color: C.text2 }}>{t('auth.completeProfileSub')}</p>
      </div>

      <div className="max-h-[420px] overflow-y-auto pr-1 space-y-5 scrollbar-thin">
        {/* Host Fields */}
        {isHost && (
          <div className="space-y-4" data-testid="host-fields">
            <span className="text-sm font-semibold" style={{ color: C.text }}>{t('auth.hostDetails')}</span>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.podcastName')} *</Label>
              <Input name="podcast_name" placeholder="The Amazing Podcast" value={formData.podcast_name} onChange={handleChange}
                className="h-11 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-sm focus:border-[#8B5CF6]/40"
                data-testid="podcast-name-input" />
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.showDesc')}</Label>
              <textarea name="show_description" placeholder="What's your show about?" value={formData.show_description} onChange={handleChange}
                rows={2} className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-sm resize-none focus:border-[#8B5CF6]/40 focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]/15"
                data-testid="show-description-input" />
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.topicsCovered')}</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="topics-covered-select">
                {TOPIC_OPTIONS.map(t => (
                  <button key={t} type="button" onClick={() => toggleTopic(t, 'topics_covered')}
                    className="px-2.5 py-1 rounded text-[11px] font-medium border transition-all"
                    style={{
                      background: formData.topics_covered.includes(t) ? `${C.purple}20` : 'transparent',
                      borderColor: formData.topics_covered.includes(t) ? `${C.purple}50` : C.border,
                      color: formData.topics_covered.includes(t) ? C.purple : C.text2,
                    }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.guestTypesWanted')}</Label>
              <Input name="guest_types_wanted" placeholder="e.g. Industry experts, authors, entrepreneurs"
                value={formData.guest_types_wanted} onChange={handleChange}
                className="h-11 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-sm focus:border-[#8B5CF6]/40"
                data-testid="guest-types-input" />
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.platformLinks')}</Label>
              <div className="flex gap-2">
                <Input placeholder="https://open.spotify.com/show/..." value={formData._platformUrl}
                  onChange={(e) => set('_platformUrl', e.target.value)}
                  className="h-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-xs flex-1 focus:border-[#8B5CF6]/40"
                  data-testid="platform-url-input" />
                <Button type="button" size="sm" className="h-10 px-3 rounded-xl text-xs"
                  style={{ background: `${C.purple}20`, color: C.purple }}
                  onClick={() => {
                    if (formData._platformUrl) {
                      const domain = new URL(formData._platformUrl).hostname.replace('www.', '').split('.')[0];
                      set('platform_links', { ...formData.platform_links, [domain]: formData._platformUrl });
                      set('_platformUrl', '');
                    }
                  }}>Add</Button>
              </div>
              {Object.entries(formData.platform_links).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {Object.entries(formData.platform_links).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-white/[0.06] text-white/60">
                      <LinkIcon className="w-2.5 h-2.5" /> {k}
                      <button type="button" onClick={() => {
                        const updated = { ...formData.platform_links };
                        delete updated[k];
                        set('platform_links', updated);
                      }} className="ml-0.5 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Divider for Both role */}
        {formData.role === 'both' && <div className="border-t my-2" style={{ borderColor: C.border }} />}

        {/* Guest Fields */}
        {isGuest && (
          <div className="space-y-4" data-testid="guest-fields">
            <span className="text-sm font-semibold" style={{ color: C.text }}>{t('auth.guestDetails')}</span>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.headline')} *</Label>
              <Input name="headline" placeholder="e.g. AI Expert & Startup Advisor"
                value={formData.headline} onChange={handleChange}
                className="h-11 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-sm focus:border-[#22D3EE]/40"
                data-testid="headline-input" />
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.bio')}</Label>
              <textarea name="bio" placeholder="Tell hosts why they should book you..." value={formData.bio} onChange={handleChange}
                rows={2} className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-sm resize-none focus:border-[#22D3EE]/40 focus:outline-none focus:ring-1 focus:ring-[#22D3EE]/15"
                data-testid="bio-input" />
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.expertiseTopics')}</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="expertise-topics-select">
                {TOPIC_OPTIONS.map(t => (
                  <button key={t} type="button" onClick={() => toggleTopic(t, 'expertise_topics')}
                    className="px-2.5 py-1 rounded text-[11px] font-medium border transition-all"
                    style={{
                      background: formData.expertise_topics.includes(t) ? `${C.cyan}20` : 'transparent',
                      borderColor: formData.expertise_topics.includes(t) ? `${C.cyan}50` : C.border,
                      color: formData.expertise_topics.includes(t) ? C.cyan : C.text2,
                    }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.prevAppearances')}</Label>
              <Input name="previous_appearances" placeholder="e.g. The Tim Ferriss Show, Joe Rogan Experience"
                value={formData.previous_appearances} onChange={handleChange}
                className="h-11 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-sm focus:border-[#22D3EE]/40"
                data-testid="previous-appearances-input" />
            </div>

            <div className="space-y-2">
              <Label style={{ color: C.text2 }} className="text-xs">{t('auth.socialLinks')}</Label>
              <div className="flex gap-2">
                <Input placeholder="https://twitter.com/yourhandle" value={formData._socialUrl}
                  onChange={(e) => set('_socialUrl', e.target.value)}
                  className="h-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 rounded-xl text-xs flex-1 focus:border-[#22D3EE]/40"
                  data-testid="social-url-input" />
                <Button type="button" size="sm" className="h-10 px-3 rounded-xl text-xs"
                  style={{ background: `${C.cyan}20`, color: C.cyan }}
                  onClick={() => {
                    if (formData._socialUrl) {
                      try {
                        const domain = new URL(formData._socialUrl).hostname.replace('www.', '').split('.')[0];
                        set('social_links', { ...formData.social_links, [domain]: formData._socialUrl });
                      } catch {
                        set('social_links', { ...formData.social_links, ['link']: formData._socialUrl });
                      }
                      set('_socialUrl', '');
                    }
                  }}>Add</Button>
              </div>
              {Object.entries(formData.social_links).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {Object.entries(formData.social_links).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-white/[0.06] text-white/60">
                      <Globe className="w-2.5 h-2.5" /> {k}
                      <button type="button" onClick={() => {
                        const updated = { ...formData.social_links };
                        delete updated[k];
                        set('social_links', updated);
                      }} className="ml-0.5 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer select-none pt-2" data-testid="privacy-consent-row">
        <input
          type="checkbox"
          checked={agreedToPrivacy}
          onChange={(e) => setAgreedToPrivacy(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border cursor-pointer accent-[#8B5CF6]"
          style={{ borderColor: C.border }}
          data-testid="privacy-consent-checkbox"
        />
        <span className="text-[12px] leading-relaxed" style={{ color: C.text2 }}>
          I agree to the{' '}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 font-semibold hover:opacity-80"
            style={{ color: C.cyan }}
            data-testid="privacy-consent-link"
          >
            Privacy Policy
          </Link>
          {' '}and{' '}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 font-semibold hover:opacity-80"
            style={{ color: C.cyan }}
            data-testid="terms-consent-link"
          >
            Terms &amp; Conditions
          </Link>
          , and consent to the processing of my personal information as described.
        </span>
      </label>

      <Button type="submit" className="w-full h-12 text-base rounded-xl"
        style={{ background: C.purple, color: '#fff', opacity: agreedToPrivacy ? 1 : 0.6 }}
        disabled={loading || !agreedToPrivacy} data-testid="submit-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t('auth.createAccountBtn')} <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </div>
  );

  /* ─── Login Form ─── */
  const LoginForm = () => (
    <div className="space-y-5" data-testid="login-form">
      <div className="mb-2">
        <h1 className="text-2xl font-bold mb-2" style={{ color: C.text }} data-testid="auth-heading">{t('auth.signIn')}</h1>
        <p className="text-sm" style={{ color: C.text2 }}>{t('auth.credentials')}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm" style={{ color: C.text2 }}>{t('auth.email')}</Label>
        <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange}
          className="h-12 rounded-xl"
          style={{ background: 'var(--pd-card-bg)', borderColor: C.border, color: C.text }}
          data-testid="email-input" />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password" className="text-sm" style={{ color: C.text2 }}>{t('auth.password')}</Label>
          <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs font-medium transition-colors hover:opacity-80"
            style={{ color: C.purple }} data-testid="forgot-password-link">
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="Your password" value={formData.password} onChange={handleChange}
            className="h-12 rounded-xl pr-12"
            style={{ background: 'var(--pd-card-bg)', borderColor: C.border, color: C.text }}
            data-testid="password-input" />
          <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: C.muted }} data-testid="toggle-password-login">
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <Button type="submit" className="w-full h-12 text-base rounded-xl mt-2"
        style={{ background: C.purple, color: '#fff' }}
        disabled={loading} data-testid="submit-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t('auth.signInBtn')} <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </div>
  );

  /* ─── Progress Indicator ─── */
  const StepIndicator = () => (
    <div className="flex items-center gap-2 mb-6" data-testid="step-indicator">
      {[1, 2, 3].map(s => (
        <React.Fragment key={s}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= s ? 'text-white' : 'text-white/30'}`}
            style={{ background: step >= s ? C.purple : 'rgba(255,255,255,0.06)', border: step >= s ? 'none' : `1px solid ${C.border}` }}>
            {step > s ? <CheckCircle className="w-4 h-4" /> : s}
          </div>
          {s < 3 && <div className="flex-1 h-0.5 rounded-full transition-all" style={{ background: step > s ? C.purple : 'rgba(255,255,255,0.08)' }} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: "transparent" }} data-testid="auth-page">
      <SEO title={isRegister ? 'Sign Up' : 'Sign In'} description="Join the #1 podcast host and guest marketplace. Create your free profile and start connecting with podcasters." path="/auth" />

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block logo-container">
            <img src={LOGO_LOCKUP} alt="Podcasters.Directory" className="h-14 object-contain logo-dark" />
            <img src={LOGO_LIGHT} alt="Podcasters.Directory" className="h-14 object-contain logo-light" />
          </Link>
        </div>

        <div className="rounded-2xl p-8 sm:p-10 border" style={{ background: 'var(--pd-card-bg)', borderColor: C.border }}>
          {/* Step Indicator (register only) */}
          {isRegister && <StepIndicator />}

          {/* Back button for step 2/3 */}
          {isRegister && step > 1 && (
            <button type="button" onClick={() => setStep(step - 1)}
              className="flex items-center gap-3 text-sm font-bold mb-6 hover:opacity-80 transition-opacity"
              style={{ color: C.text }} data-testid="back-step-btn">
              <span className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors" style={{ background: 'var(--pd-surface)' }}>
                <ArrowLeft className="w-5 h-5" />
              </span>
              {t('auth.back')}
            </button>
          )}

          <form onSubmit={handleSubmit} data-testid="auth-form">
            {/* Referral Banner */}
            {isRegister && referralInfo && (
              <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.2)' }} data-testid="referral-banner">
                <Gift className="w-5 h-5 shrink-0" style={{ color: C.cyan }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: C.text }}>Referred by <span style={{ color: C.cyan }}>{referralInfo.referrerName}</span></p>
                  <p className="text-[10px]" style={{ color: C.muted }}>You were invited to join Podcasters.Directory!</p>
                </div>
              </div>
            )}

            {isRegister ? (
              step === 1 ? RoleStep() : step === 2 ? BasicsStep() : RoleFieldsStep()
            ) : (
              LoginForm()
            )}
          </form>

          {/* Toggle Mode */}
          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: C.muted }}>
              {isRegister ? t('auth.haveAccount') : t('auth.noAccount')}
              <button type="button" onClick={() => { setIsRegister(!isRegister); setStep(1); }}
                className="ml-1.5 font-medium transition-colors"
                style={{ color: C.cyan }}
                data-testid="toggle-auth-mode">
                {isRegister ? t('auth.signIn') : t('auth.signUp')}
              </button>
            </p>
          </div>

          {!isRegister && (
            <div className="mt-6 pt-6 border-t" style={{ borderColor: C.border }}>
              <p className="text-xs text-center mb-2" style={{ color: C.muted }}>{t('auth.startFree')}</p>
              <div className="flex justify-center gap-5 text-xs" style={{ color: C.text2 }}>
                <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#10B981]" /> {t('auth.freeProfile')}</span>
                <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#10B981]" /> {t('auth.smartMatching')}</span>
                <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#10B981]" /> {t('auth.verifiedBadge')}</span>
              </div>
            </div>
          )}

          {isRegister && step === 1 && (
            <div className="mt-6 text-center">
              <p className="text-xs" style={{ color: C.muted }}>Join thousands of podcasters growing their network</p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs transition-colors" style={{ color: C.muted }}>{t('auth.backToHome')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
